import {
  Button, Typography, useTheme,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useTranslation } from 'react-i18next';
import { TopicsCard } from '../TopicsCard';
import { useIsMobile } from '../../../common/hooks/useResponsive';
import ContentSearchView from './ContentSearchView';
import ContentSearchPage from './ContentSearchPage';
import { HOME_ROUTE, RESOURCES_ROUTE } from '../../../routes/namedRoutes';
import { TestIds } from '../../../assets/testIds';
import { useSafeBack } from '../../../common/hooks/useSafeBack';
import { useOptimizelyFlag } from '../../../common/hooks/useOptimizelyFlag';
import { EdgeHomeNewDesignFlag } from '../../../common/OptimizelyFlagsConstants';

export function ContentSearch() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query') ?? undefined;
  const businessGoalTag = searchParams.get('businessGoal') ?? undefined;
  const topicTag = searchParams.get('topic') ?? undefined;
  const [searchTerm, setSearchTerm] = useState(query);
  const [hasNoResults, setHasNoResults] = useState(false);
  const {
    decision: edgeHomeNewDesignDecision,
    isDecisionReady: isEdgeHomeNewDesignDecisionReady,
  } = useOptimizelyFlag({
    flag: EdgeHomeNewDesignFlag,
  });
  const isEdgeHomeNewDesignEnabled = isEdgeHomeNewDesignDecisionReady &&
    !!edgeHomeNewDesignDecision?.enabled;
  // In the top navigation search belongs to Resources, so Back returns there
  const safeBack = useSafeBack(
    HOME_ROUTE,
    isEdgeHomeNewDesignEnabled ? RESOURCES_ROUTE : undefined,
  );

  // get search box input value
  const getValue = () => query || businessGoalTag || topicTag;

  const [searchInputValue, setSearchInputValue] = useState(getValue());

  const handleSubmit = (value: string | undefined) => {
    const params = new URLSearchParams();
    const trimmedValue = value?.trim();
    if (!trimmedValue) return;

    params.set('query', trimmedValue);
    const route = `/search?${params.toString()}`;
    navigate(route);
  };

  useEffect(() => {
    setSearchInputValue(getValue());
    setSearchTerm(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1.5),
          mt: { xs: theme.spacing(2), md: theme.spacing(4) },
          px: { md: theme.spacing(4) },
        }}
      >
        <Button
          data-testid={TestIds.contentArticleView.backBtn}
          startIcon={<ArrowBackIosIcon sx={{ fontSize: `${theme.spacing(2)} !important` }} />}
          sx={{
            minWidth: theme.spacing(8),
            height: theme.spacing(5.5),
            padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
            border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
            color: theme.palette.grey[900],
            '&:hover': {
              borderColor: theme.palette.grey[200],
            },
            backgroundColor: theme.palette.common.white,
            flexShrink: 0,
          }}
          onClick={safeBack}
        >
          {t('button.back')}
        </Button>
        <Box
          sx={{
            flex: 1,
          }}
        >
          <ContentSearchView
            onSubmit={handleSubmit}
            value={searchInputValue}
          />
        </Box>
      </Box>

      {/* for mobile view if there is no search term, show the spotlight topics */}
      {(isMobile && !searchTerm && !businessGoalTag && !topicTag) &&
        <Box
          sx={{
            padding: `${theme.spacing(3)} ${theme.spacing(0)} ${theme.spacing(0)} ${theme.spacing(0)}`,
          }}
        >
          <TopicsCard />
        </Box>
      }
      {(hasNoResults && (searchTerm || businessGoalTag || topicTag)) && <Box>
        <Box sx={{
          textAlign: 'center',
          marginTop: theme.spacing(4),
          marginBottom: theme.spacing(4),
          color: theme.palette.grey[500],
        }}>
          <Typography variant='body5'>{t('content.feed.noResultMessage')}</Typography>
          <Typography variant='body5'>{t('content.feed.alternativeSearchMessage')}</Typography>
          <Typography variant='body5'>{t('content.feed.tryBelowMessage')}</Typography>
        </Box>
      </Box>}
      {(searchTerm || businessGoalTag || topicTag) &&
        <ContentSearchPage
          searchTerm={searchTerm}
          topic={topicTag}
          businessGoal={businessGoalTag}
          fullWidthContainer={true}
          contentViewBaseUrl={'/search'}
          setHasNoResults={setHasNoResults}
          showSearchBar={false}
        />
      }
    </>
  );
}
